import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useWebSocket } from "../../hooks/useWebSocket";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import ProductCustomizationModal from "../../components/ProductCustomizationModal";
import {
  Utensils, LayoutGrid, Bell, ClipboardList, LogOut, Plus, Minus,
  Check, ShoppingCart, ArrowLeft, Search, X, Coffee, Clock, Volume2, VolumeX
} from "lucide-react";
import { cn, formatCurrency } from "../../lib/utils";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tabs
const TABS = [
  { key: "tables", label: "Tables", icon: LayoutGrid },
  { key: "ready", label: "Ready", icon: Bell },
  { key: "active", label: "Active", icon: ClipboardList },
];

const getTimeSince = (dateStr) => {
  if (!dateStr) return "—";
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  return `${Math.floor(diff / 3600)}h`;
};

// How long completed orders stay on screen after all items served (in minutes)
const CLEANUP_MINUTES = 5;

const WaiterDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Data
  const [tables, setTables] = useState([]);
  const [orders, setOrders] = useState([]);
  const [menu, setMenu] = useState({ categories: [], items: [] });
  const [restaurant, setRestaurant] = useState(null);

  // UI
  const [tab, setTab] = useState("tables");
  const [tableDetail, setTableDetail] = useState(null); // when waiter taps a table
  const [orderSheet, setOrderSheet] = useState(false);  // new-order builder
  const [orderTable, setOrderTable] = useState(null);   // table being ordered for
  const [cart, setCart] = useState([]);                 // [{item, quantity}]
  const [activeCategory, setActiveCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [soundOn, setSoundOn] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [customizeItem, setCustomizeItem] = useState(null);  // item being customized
  const [sessionsSent, setSessionsSent] = useState({}); // tableNum -> count of partial sends this session
  const [assistanceRequests, setAssistanceRequests] = useState([]); // active call-waiter requests
  const [, setTick] = useState(0); // forces rerender for time labels & cleanup
  const bellRef = useRef(null);

  // ===== Persistent cart per table — survives refresh =====
  const cartStorageKey = useMemo(() => orderTable ? `dalabsom_waiter_cart_${user?.restaurant_id}_T${orderTable.table_number}` : null, [orderTable, user?.restaurant_id]);
  // Load persisted cart whenever the active table changes
  useEffect(() => {
    if (!cartStorageKey) return;
    try {
      const saved = localStorage.getItem(cartStorageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) setCart(parsed);
      } else {
        setCart([]);
      }
    } catch (e) {
      console.error("Failed to load cart:", e);
      setCart([]);
    }
  }, [cartStorageKey]);
  // Persist cart on changes
  useEffect(() => {
    if (!cartStorageKey) return;
    try {
      if (cart.length > 0) localStorage.setItem(cartStorageKey, JSON.stringify(cart));
      else localStorage.removeItem(cartStorageKey);
    } catch (e) { /* quota or private mode — ignore */ }
  }, [cart, cartStorageKey]);

  // Force re-render every 30s for elapsed-time labels and cleanup
  useEffect(() => {
    const i = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(i);
  }, []);

  // Keep-alive ping every 4 minutes (heartbeat — backend stays warm)
  useEffect(() => {
    const ping = () => { axios.get(`${API}/health`).catch(() => {}); };
    ping();
    const i = setInterval(ping, 4 * 60 * 1000);
    return () => clearInterval(i);
  }, []);

  // Sound — soft chime for ready orders
  useEffect(() => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    bellRef.current = () => {
      if (!soundOn) return;
      const play = (f, t, d) => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.frequency.value = f; o.type = "sine";
        g.gain.setValueAtTime(0.25, t);
        g.gain.exponentialRampToValueAtTime(0.01, t + d);
        o.start(t); o.stop(t + d);
      };
      const n = ctx.currentTime;
      play(1046, n, 0.15); play(1318, n + 0.18, 0.15); play(1568, n + 0.36, 0.3);
    };
    return () => { ctx.close(); };
  }, [soundOn]);

  // Fetch everything
  const fetchAll = useCallback(async () => {
    try {
      const [oRes, tRes, mRes, rRes, aRes] = await Promise.all([
        axios.get(`${API}/orders`),
        axios.get(`${API}/restaurant/tables`),
        axios.get(`${API}/kitchen/menu`),
        axios.get(`${API}/restaurant/profile`),
        axios.get(`${API}/restaurant/assistance-requests`).catch(() => ({ data: [] })),
      ]);
      setOrders(oRes.data || []);
      setTables(tRes.data || []);
      setMenu(mRes.data || { categories: [], items: [] });
      setRestaurant(rRes.data);
      setAssistanceRequests(aRes.data || []);
    } catch (e) {
      toast.error("Failed to load");
    }
  }, []);

  const resolveAssistance = async (reqId) => {
    try {
      await axios.put(`${API}/restaurant/assistance-requests/${reqId}?status=resolved`);
      setAssistanceRequests(prev => prev.filter(r => r.id !== reqId));
      toast.success("Marked as assisted");
    } catch (e) { toast.error("Failed"); }
  };

  useEffect(() => { fetchAll(); const i = setInterval(fetchAll, 30000); return () => clearInterval(i); }, [fetchAll]);

  // WebSocket — react to kitchen updates in real-time
  const onMessage = useCallback((msg) => {
    if (msg.type === "call_waiter" && msg.request) {
      // Customer requested assistance
      if (navigator.vibrate) navigator.vibrate([200, 100, 200, 100, 200]);
      bellRef.current?.();
      const r = msg.request;
      toast.warning(`🛎️ T${r.table_number || r.qr_name || "?"} needs assistance`, { duration: 8000 });
      setAssistanceRequests(prev => {
        const filtered = prev.filter(x => x.id !== r.id);
        return [r, ...filtered];
      });
      return;
    }
    if (msg.type === "call_waiter_update" && msg.request) {
      setAssistanceRequests(prev => {
        if (msg.request.status === "resolved") return prev.filter(x => x.id !== msg.request.id);
        return prev.map(x => x.id === msg.request.id ? msg.request : x);
      });
      return;
    }
    if (["new_order", "order_updated", "order_status_update", "item_status_update", "payment_confirmed", "payment_verified", "payment_proof_submitted"].includes(msg.type)) {
      if (msg.order) {
        setOrders(prev => {
          const exists = prev.some(o => o.id === msg.order.id);
          return exists ? prev.map(o => o.id === msg.order.id ? msg.order : o) : [msg.order, ...prev];
        });
        // If any item just transitioned to "ready", ding + vibrate
        if (msg.type === "item_status_update" && (msg.order.items || []).some(it => it.item_status === "ready")) {
          bellRef.current?.();
          if (navigator.vibrate) navigator.vibrate([180, 90, 180]);
          toast.success(`T${msg.order.table_number} · Order ready to serve`, { duration: 3500 });
        }
        // Kitchen accepted (any item moved to preparing) — light buzz
        if (msg.type === "item_status_update" && (msg.order.items || []).some(it => it.item_status === "preparing")) {
          if (navigator.vibrate) navigator.vibrate(50);
        }
        // Payment confirmed
        if (msg.type === "payment_verified" && msg.order.payment_status === "paid") {
          if (navigator.vibrate) navigator.vibrate(100);
          toast.success(`T${msg.order.table_number} · Payment confirmed ✓`, { duration: 3000 });
        }
      }
    }
  }, []);
  useWebSocket(user?.restaurant_id, onMessage);

  // ===== ACTIVE ORDERS — filter ones to show in waiter UI =====
  // Show: orders that are not completed, OR completed within last CLEANUP_MINUTES
  const visibleOrders = useMemo(() => {
    const now = Date.now();
    return orders.filter(o => {
      if (o.status !== "completed" && o.status !== "cancelled") return true;
      // Keep recently-served orders briefly
      const allServedAts = (o.items || []).map(i => i.served_at).filter(Boolean);
      if (allServedAts.length === 0) return false;
      const latest = Math.max(...allServedAts.map(s => new Date(s).getTime()));
      return now - latest < CLEANUP_MINUTES * 60 * 1000;
    }).filter(o => o.order_type === "eat_in"); // Waiter only handles dine-in
  }, [orders]);

  // Group orders by table
  const ordersByTable = useMemo(() => {
    const map = {};
    visibleOrders.forEach(o => {
      const t = o.table_number || "—";
      if (!map[t]) map[t] = [];
      map[t].push(o);
    });
    return map;
  }, [visibleOrders]);

  const readyOrders = useMemo(() =>
    visibleOrders.filter(o => (o.items || []).some(i => i.item_status === "ready")),
  [visibleOrders]);

  const readyItemsCount = useMemo(() =>
    readyOrders.reduce((n, o) => n + (o.items || []).filter(i => i.item_status === "ready").length, 0),
  [readyOrders]);

  // ===== Actions =====
  const markServed = async (orderId, itemId) => {
    try {
      await axios.put(`${API}/orders/${orderId}/items/${itemId}/status`, { item_status: "served" });
      toast.success("Marked served");
    } catch (e) { toast.error("Failed to mark served"); }
  };

  const markAllReadyServed = async (order) => {
    const ready = (order.items || []).filter(i => i.item_status === "ready");
    if (ready.length === 0) return;
    try {
      await Promise.all(ready.map(i => axios.put(`${API}/orders/${order.id}/items/${i.id}/status`, { item_status: "served" })));
      toast.success(`Delivered ${ready.length} item${ready.length > 1 ? "s" : ""} to T${order.table_number}`);
    } catch (e) { toast.error("Failed to mark served"); }
  };

  // ===== Cart helpers =====
  // addToCart receives a full customized line from the customization modal
  const addLineToCart = (line) => {
    setCart(prev => [...prev, line]);
    toast.success(`${line.name} added`, { duration: 1200 });
  };

  const incLine = (lineId) => setCart(prev => prev.map(c => c.id === lineId ? { ...c, quantity: c.quantity + 1 } : c));
  const decLine = (lineId) => setCart(prev => prev.flatMap(c => {
    if (c.id !== lineId) return [c];
    if (c.quantity <= 1) return [];
    return [{ ...c, quantity: c.quantity - 1 }];
  }));

  const cartTotal = cart.reduce((s, c) => s + c.price * c.quantity, 0);
  const cartCount = cart.reduce((s, c) => s + c.quantity, 0);

  const submitOrder = async () => {
    if (!orderTable || cart.length === 0) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/cashier/orders`, {
        order_type: "eat_in",
        table_number: orderTable.table_number,
        customer_phone: null,
        items: cart.map(c => ({
          menu_item_id: c.menu_item_id,
          quantity: c.quantity,
          variant_id: c.variant_id || null,
          selected_extras: c.selected_extras || [],
          notes: c.notes || "",
        })),
        payment_method: "pay_at_cashier",
      });
      const newSentCount = (sessionsSent[orderTable.table_number] || 0) + 1;
      setSessionsSent(prev => ({ ...prev, [orderTable.table_number]: newSentCount }));
      toast.success(`✓ Sent to kitchen — T${orderTable.table_number} (round ${newSentCount})`);
      // Clear cart but KEEP session open so waiter can keep adding more
      setCart([]);
      if (cartStorageKey) localStorage.removeItem(cartStorageKey);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send order");
    } finally { setSubmitting(false); }
  };

  // Finalize: close the session and return to tables
  const finalizeTable = () => {
    if (cart.length > 0) {
      const yes = window.confirm("You still have items in the cart that haven't been sent. Finalize anyway? Unsent items will be discarded.");
      if (!yes) return;
    }
    if (cartStorageKey) localStorage.removeItem(cartStorageKey);
    setCart([]);
    setOrderSheet(false);
    setOrderTable(null);
    setSessionsSent(prev => { const c = { ...prev }; delete c[orderTable.table_number]; return c; });
    setTab("active");
  };

  // Keep editing — temporarily close menu sheet but keep session table alive (cart already persisted in localStorage)
  const keepEditing = () => {
    setOrderSheet(false);
    // orderTable stays set; cart stays persisted
    toast.success(`Session kept open for T${orderTable.table_number}`, { duration: 1500 });
  };

  // ===== Open sessions (from localStorage carts) — for table badge display =====
  const [openSessions, setOpenSessions] = useState({});
  const refreshOpenSessions = useCallback(() => {
    if (!user?.restaurant_id) return;
    const prefix = `dalabsom_waiter_cart_${user.restaurant_id}_T`;
    const out = {};
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k || !k.startsWith(prefix)) continue;
        const tableNum = k.substring(prefix.length);
        const v = JSON.parse(localStorage.getItem(k) || "[]");
        const count = Array.isArray(v) ? v.reduce((n, c) => n + (c.quantity || 0), 0) : 0;
        if (count > 0) out[tableNum] = count;
      }
    } catch (e) { /* ignore */ }
    setOpenSessions(out);
  }, [user?.restaurant_id]);
  useEffect(() => { refreshOpenSessions(); }, [refreshOpenSessions, cart, orderTable]);

  // ===== Menu filter =====
  const filteredItems = useMemo(() => {
    let items = menu.items.filter(i => i.is_available !== false);
    if (activeCategory !== "all") items = items.filter(i => i.category_id === activeCategory);
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(i => i.name.toLowerCase().includes(q));
    }
    return items;
  }, [menu, activeCategory, search]);

  const handleLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-slate-50 pb-20" data-testid="waiter-dashboard">
      {/* Header — sticky, mobile-first */}
      <header className="sticky top-0 z-30 bg-gradient-to-r from-teal-600 to-emerald-600 text-white shadow-md">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
              <Utensils className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h1 className="font-bold text-base truncate">{restaurant?.name || "Dalabsom"}</h1>
              <p className="text-[11px] text-white/80 truncate">Waiter · {user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/20 h-9 w-9"
              onClick={() => setSoundOn(s => !s)} data-testid="toggle-sound">
              {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/20 h-9 w-9"
              onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="px-3 pt-3">
        {/* Assistance requests banner */}
        {assistanceRequests.length > 0 && (
          <div className="mb-3 bg-amber-50 border-2 border-amber-400 rounded-2xl p-3 animate-in fade-in" data-testid="assistance-banner">
            <div className="flex items-center gap-2 mb-2">
              <Bell className="w-4 h-4 text-amber-600 animate-pulse" />
              <span className="text-sm font-bold text-amber-900">
                {assistanceRequests.length} call{assistanceRequests.length > 1 ? "s" : ""} for assistance
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {assistanceRequests.map(r => (
                <div key={r.id} className="bg-white border border-amber-300 rounded-xl px-2.5 py-1.5 flex items-center gap-2 text-sm shadow-sm" data-testid={`waiter-assistance-${r.id}`}>
                  <span className="font-bold text-amber-900">T{r.table_number || r.qr_name || "?"}</span>
                  {r.reason && <span className="text-xs text-amber-700 truncate max-w-[120px]">{r.reason}</span>}
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-emerald-700"
                    onClick={() => resolveAssistance(r.id)} data-testid={`waiter-resolve-${r.id}`}>
                    <Check className="w-3 h-3 mr-1" /> Done
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "tables" && (
          <TablesTab tables={tables} ordersByTable={ordersByTable} openSessions={openSessions}
            onSelectTable={(t) => setTableDetail(t)}
            onNewOrder={(t) => { setOrderTable(t); setOrderSheet(true); setActiveCategory("all"); setSearch(""); }}
          />
        )}
        {tab === "ready" && (
          <ReadyTab readyOrders={readyOrders} onServe={markServed} onServeAll={markAllReadyServed} />
        )}
        {tab === "active" && (
          <ActiveTab orders={visibleOrders} onServe={markServed} onServeAll={markAllReadyServed}
            onOpenTable={(tableNum) => setTableDetail(tables.find(t => t.table_number === tableNum) || { table_number: tableNum })}
          />
        )}
      </main>

      {/* Bottom tab bar */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-30">
        <div className="grid grid-cols-3">
          {TABS.map(t => {
            const isActive = tab === t.key;
            const Icon = t.icon;
            const badge = t.key === "ready" ? readyItemsCount : null;
            return (
              <button key={t.key}
                className={cn("relative flex flex-col items-center gap-0.5 py-2.5 transition-colors",
                  isActive ? "text-emerald-600" : "text-slate-500"
                )}
                onClick={() => setTab(t.key)}
                data-testid={`tab-${t.key}`}>
                <Icon className="w-5 h-5" />
                <span className="text-[11px] font-semibold">{t.label}</span>
                {badge > 0 && (
                  <span className="absolute top-1.5 right-[28%] bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 animate-pulse">
                    {badge}
                  </span>
                )}
                {isActive && <span className="absolute bottom-0 left-1/4 right-1/4 h-0.5 bg-emerald-600 rounded-t" />}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Table detail sheet */}
      <Sheet open={!!tableDetail} onOpenChange={(o) => !o && setTableDetail(null)}>
        <SheetContent side="bottom" className="h-[85vh] rounded-t-3xl p-0">
          <SheetHeader className="px-4 pt-4 pb-2 border-b">
            <SheetTitle className="flex items-center justify-between">
              <span className="text-xl">Table {tableDetail?.table_number}</span>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => { setOrderTable(tableDetail); setTableDetail(null); setOrderSheet(true); setActiveCategory("all"); setSearch(""); }}
                data-testid="new-order-from-detail">
                <Plus className="w-4 h-4 mr-1" /> New Order
              </Button>
            </SheetTitle>
          </SheetHeader>
          <div className="overflow-y-auto h-[calc(85vh-72px)] px-3 py-3 space-y-3">
            {(ordersByTable[tableDetail?.table_number] || []).length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p>No active orders</p>
              </div>
            ) : (
              (ordersByTable[tableDetail?.table_number] || []).map(o => (
                <OrderItemCard key={o.id} order={o} onServe={markServed} onServeAll={markAllReadyServed} />
              ))
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* New order sheet — fast ordering with big cards (session-persistent) */}
      <Dialog open={orderSheet} onOpenChange={(open) => { if (!open) keepEditing(); }}>
        <DialogContent className="max-w-full w-full h-full sm:max-w-2xl sm:h-auto sm:max-h-[92vh] p-0 gap-0 rounded-none sm:rounded-2xl flex flex-col">
          <DialogDescription className="sr-only">Order builder for table {orderTable?.table_number}. Use Send to dispatch the current cart to the kitchen; Keep Editing to hide this window without losing the cart; Finalize to close the table session.</DialogDescription>
          <DialogHeader className="px-4 py-3 border-b shrink-0 bg-white">
            <DialogTitle className="flex items-center justify-between gap-2">
              <button onClick={keepEditing} className="flex items-center gap-2 text-base min-w-0">
                <ArrowLeft className="w-5 h-5 shrink-0" />
                <span className="truncate">T{orderTable?.table_number} · Session open</span>
              </button>
              <div className="flex items-center gap-1.5 shrink-0">
                {sessionsSent[orderTable?.table_number] > 0 && (
                  <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                    {sessionsSent[orderTable?.table_number]} sent
                  </Badge>
                )}
                <Badge className="bg-emerald-100 text-emerald-700">{cartCount} in cart</Badge>
              </div>
            </DialogTitle>
          </DialogHeader>

          {/* Already-sent items summary banner */}
          {(() => {
            const sentOrders = (ordersByTable[orderTable?.table_number] || []).filter(o => o.status !== "completed" && o.status !== "cancelled");
            if (sentOrders.length === 0) return null;
            const sentItems = sentOrders.flatMap(o => o.items || []).filter(i => !["served", "cancelled"].includes(i.item_status));
            if (sentItems.length === 0) return null;
            return (
              <div className="shrink-0 bg-amber-50 border-b border-amber-200 px-3 py-2 text-xs">
                <p className="font-bold text-amber-800 mb-1">Already sent ({sentItems.length} items, locked):</p>
                <div className="flex flex-wrap gap-1">
                  {sentItems.slice(0, 6).map(i => (
                    <span key={i.id} className="bg-white border border-amber-300 px-1.5 py-0.5 rounded text-amber-900">{i.quantity}× {i.name}</span>
                  ))}
                  {sentItems.length > 6 && <span className="text-amber-700">+{sentItems.length - 6} more</span>}
                </div>
              </div>
            );
          })()}

          {/* Search */}
          <div className="px-3 py-2 shrink-0 bg-white border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input className="pl-9" placeholder="Search menu..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="menu-search" />
            </div>
          </div>

          {/* Categories */}
          <div className="px-3 py-2 shrink-0 bg-white border-b overflow-x-auto">
            <div className="flex gap-2 min-w-max">
              <CategoryChip label="All" active={activeCategory === "all"} onClick={() => setActiveCategory("all")} />
              {menu.categories.map(c => (
                <CategoryChip key={c.id} label={c.name} icon={c.category_type === "drink" ? Coffee : Utensils}
                  active={activeCategory === c.id} onClick={() => setActiveCategory(c.id)} />
              ))}
            </div>
          </div>

          {/* Menu grid */}
          <div className="flex-1 overflow-y-auto p-3 bg-slate-50">
            {filteredItems.length === 0 ? (
              <div className="text-center py-16 text-slate-400">No items</div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {filteredItems.map(item => {
                  const lineCount = cart.filter(c => c.menu_item_id === item.id).reduce((s, c) => s + c.quantity, 0);
                  const unavailable = item.is_available === false;
                  return (
                    <button key={item.id}
                      onClick={() => !unavailable && setCustomizeItem(item)}
                      disabled={unavailable}
                      data-testid={`menu-item-${item.id}`}
                      className={cn("bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden text-left transition-all hover:shadow-md",
                        unavailable ? "opacity-50 grayscale cursor-not-allowed" : "active:scale-95"
                      )}>
                      <div className="aspect-square bg-slate-100 relative overflow-hidden">
                        {item.image_data ? (
                          <img src={item.image_data} alt={item.name} className="w-full h-full object-cover" />
                        ) : item.image_url ? (
                          <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-300">
                            <Utensils className="w-10 h-10" />
                          </div>
                        )}
                        {lineCount > 0 && (
                          <div className="absolute top-2 right-2 bg-emerald-500 text-white w-7 h-7 rounded-full flex items-center justify-center font-bold text-sm shadow">
                            {lineCount}
                          </div>
                        )}
                        {unavailable && (
                          <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                            <span className="text-[11px] font-bold bg-red-600 text-white px-2 py-1 rounded-full">UNAVAILABLE</span>
                          </div>
                        )}
                        {(item.extras?.length > 0 || item.variants?.length > 0) && !unavailable && (
                          <div className="absolute bottom-1.5 left-1.5 text-[10px] font-semibold bg-slate-900/80 text-white px-1.5 py-0.5 rounded">
                            Customizable
                          </div>
                        )}
                      </div>
                      <div className="p-2.5">
                        <p className="text-sm font-semibold text-slate-900 truncate">{item.name}</p>
                        <p className="text-emerald-600 font-bold text-sm mt-0.5">{formatCurrency(item.price)}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Cart bar */}
          <div className="shrink-0 bg-white border-t shadow-lg">
            {cart.length > 0 && (
              <div className="max-h-40 overflow-y-auto px-3 py-2 border-b">
                {cart.map(c => (
                  <div key={c.id} className="flex items-start justify-between py-1.5 gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{c.name}{c.variant_name && <span className="text-slate-400 text-xs"> · {c.variant_name}</span>}</p>
                      {c.extras_details?.length > 0 && (
                        <p className="text-xs text-emerald-600 truncate">+ {c.extras_details.map(e => e.name).join(", ")}</p>
                      )}
                      {c.notes && <p className="text-xs text-amber-600 truncate italic">"{c.notes}"</p>}
                      <p className="text-xs text-slate-500">{formatCurrency(c.price)} × {c.quantity}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => decLine(c.id)}><Minus className="w-3 h-3" /></Button>
                      <span className="w-6 text-center text-sm font-bold">{c.quantity}</span>
                      <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => incLine(c.id)}><Plus className="w-3 h-3" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="px-3 py-3">
              {cart.length > 0 && (
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-slate-500">New items total</span>
                  <span className="text-lg font-bold text-slate-900">{formatCurrency(cartTotal)}</span>
                </div>
              )}
              <div className="grid grid-cols-3 gap-2">
                <Button variant="outline" className="h-11 text-xs" onClick={keepEditing} data-testid="keep-editing-btn">
                  Keep Editing
                </Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700 h-11 text-xs font-bold"
                  onClick={submitOrder} disabled={submitting || cart.length === 0} data-testid="submit-order-btn">
                  <ShoppingCart className="w-4 h-4 mr-1" />
                  {submitting ? "Sending..." : "Send"}
                </Button>
                <Button variant="outline" className="h-11 text-xs border-red-300 text-red-600 hover:bg-red-50"
                  onClick={finalizeTable} data-testid="finalize-table-btn">
                  Finalize
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Shared product customization modal */}
      <ProductCustomizationModal
        item={customizeItem}
        open={!!customizeItem}
        onClose={() => setCustomizeItem(null)}
        onAdd={addLineToCart}
        brandColor="#059669"
      />
    </div>
  );
};

// ===== SUB-COMPONENTS =====

const CategoryChip = ({ label, icon: Icon, active, onClick }) => (
  <button onClick={onClick}
    className={cn("px-3.5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap flex items-center gap-1.5 transition-colors",
      active ? "bg-emerald-600 text-white" : "bg-white border border-slate-200 text-slate-700"
    )}>
    {Icon && <Icon className="w-3.5 h-3.5" />}
    {label}
  </button>
);

const TablesTab = ({ tables, ordersByTable, onSelectTable, onNewOrder, openSessions = {} }) => {
  if (tables.length === 0) {
    return (
      <div className="text-center py-16 text-slate-400">
        <LayoutGrid className="w-12 h-12 mx-auto mb-3 opacity-40" />
        <p className="font-medium">No tables configured yet</p>
        <p className="text-xs mt-1">Ask your owner to set up tables.</p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-3" data-testid="tables-grid">
      {tables.filter(t => t.is_active !== false).map(t => {
        const list = ordersByTable[t.table_number] || [];
        const readyCount = list.reduce((n, o) => n + (o.items || []).filter(i => i.item_status === "ready").length, 0);
        const totalActiveItems = list.reduce((n, o) => n + (o.items || []).filter(i => !["served", "cancelled"].includes(i.item_status)).length, 0);
        const isOccupied = list.length > 0;
        const hasOpenSession = !!openSessions[t.table_number];
        return (
          <div key={t.id || t.table_number}
            className={cn("rounded-2xl p-4 shadow-sm border transition-all",
              readyCount > 0 ? "bg-amber-50 border-amber-300 ring-2 ring-amber-300/50 animate-pulse" :
              hasOpenSession ? "bg-blue-50 border-blue-300" :
              isOccupied ? "bg-white border-emerald-200" : "bg-white border-slate-200"
            )} data-testid={`table-${t.table_number}`}>
            <button className="w-full text-left" onClick={() => onSelectTable(t)}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-3xl font-bold text-slate-900">T{t.table_number}</p>
                  <p className="text-xs text-slate-500">
                    {t.qr_name || `${t.capacity || 4} seats`}
                    {t.floor && <span> · {t.floor}</span>}
                    {t.zone && <span> · {t.zone}</span>}
                  </p>
                </div>
                <div className={cn("w-3 h-3 rounded-full",
                  readyCount > 0 ? "bg-amber-500" : hasOpenSession ? "bg-blue-500" : isOccupied ? "bg-emerald-500" : "bg-slate-300"
                )} />
              </div>
              {readyCount > 0 ? (
                <p className="text-xs font-bold text-amber-700 mb-2">{readyCount} READY · DELIVER NOW</p>
              ) : hasOpenSession ? (
                <p className="text-xs font-bold text-blue-700 mb-2">{openSessions[t.table_number]} in cart</p>
              ) : isOccupied ? (
                <p className="text-xs text-slate-600 mb-2">{totalActiveItems} item{totalActiveItems !== 1 ? "s" : ""} in progress</p>
              ) : (
                <p className="text-xs text-slate-400 mb-2">Free</p>
              )}
            </button>
            <Button size="sm" className={cn("w-full h-8 text-xs", hasOpenSession ? "bg-blue-600 hover:bg-blue-700" : "bg-emerald-600 hover:bg-emerald-700")}
              onClick={() => onNewOrder(t)} data-testid={`new-order-table-${t.table_number}`}>
              <Plus className="w-3 h-3 mr-1" /> {hasOpenSession ? "Resume" : "New Order"}
            </Button>
          </div>
        );
      })}
    </div>
  );
};

const ReadyTab = ({ readyOrders, onServe, onServeAll }) => {
  if (readyOrders.length === 0) {
    return (
      <div className="text-center py-16 text-slate-400">
        <Bell className="w-12 h-12 mx-auto mb-3 opacity-40" />
        <p className="font-medium">No orders ready</p>
        <p className="text-xs mt-1">You'll be notified when the kitchen finishes.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="ready-list">
      {readyOrders.map(o => {
        const readyItems = (o.items || []).filter(i => i.item_status === "ready");
        return (
          <div key={o.id} className="bg-white rounded-2xl shadow-sm border-2 border-amber-300 overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-3 flex items-center justify-between">
              <div>
                <p className="text-xs font-medium opacity-90">Table</p>
                <p className="text-3xl font-bold leading-tight">T{o.table_number}</p>
              </div>
              <div className="text-right">
                <p className="text-xs opacity-90">Order</p>
                <p className="font-mono font-bold text-sm">#{o.order_number?.split("-")[1]}</p>
              </div>
            </div>
            <div className="p-3 space-y-2">
              {readyItems.map(i => (
                <div key={i.id} className="flex items-center gap-2 bg-amber-50 rounded-xl p-2.5 border border-amber-200">
                  <span className="bg-amber-500 text-white px-2 py-0.5 rounded-md text-xs font-bold">{i.quantity}×</span>
                  <span className="flex-1 text-sm font-medium text-slate-900 truncate">{i.name}</span>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8 px-3 text-xs"
                    onClick={() => onServe(o.id, i.id)} data-testid={`serve-${i.id}`}>
                    <Check className="w-3.5 h-3.5 mr-1" /> Serve
                  </Button>
                </div>
              ))}
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700 h-10 font-bold mt-2"
                onClick={() => onServeAll(o)} data-testid={`serve-all-${o.id}`}>
                <Check className="w-4 h-4 mr-2" /> Delivered All to T{o.table_number}
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ActiveTab = ({ orders, onServe, onServeAll, onOpenTable }) => {
  if (orders.length === 0) {
    return (
      <div className="text-center py-16 text-slate-400">
        <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-40" />
        <p className="font-medium">No active orders</p>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="active-list">
      {orders.map(o => <OrderItemCard key={o.id} order={o} onServe={onServe} onServeAll={onServeAll} onOpenTable={onOpenTable} />)}
    </div>
  );
};

const ItemStatusPill = ({ status }) => {
  const cfg = {
    waiting: { label: "Waiting", cls: "bg-slate-200 text-slate-700" },
    preparing: { label: "Cooking", cls: "bg-amber-100 text-amber-800" },
    ready: { label: "READY", cls: "bg-emerald-100 text-emerald-800 font-bold animate-pulse" },
    served: { label: "Served", cls: "bg-slate-100 text-slate-500 line-through" },
    cancelled: { label: "Cancelled", cls: "bg-red-100 text-red-700" },
  }[status || "waiting"] || { label: status, cls: "bg-slate-100 text-slate-600" };
  return <span className={cn("text-[10px] px-1.5 py-0.5 rounded font-medium", cfg.cls)}>{cfg.label}</span>;
};

const OrderItemCard = ({ order, onServe, onServeAll, onOpenTable }) => {
  const readyCount = (order.items || []).filter(i => i.item_status === "ready").length;
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden" data-testid={`order-${order.id}`}>
      <div className="px-4 py-2.5 bg-slate-50 border-b flex items-center justify-between">
        <button className="flex items-center gap-2 min-w-0" onClick={() => onOpenTable?.(order.table_number)}>
          <span className="bg-emerald-600 text-white text-xs font-bold px-2 py-0.5 rounded">T{order.table_number}</span>
          <span className="font-mono text-xs text-slate-500">#{order.order_number?.split("-")[1]}</span>
        </button>
        <div className="flex items-center gap-1 text-xs text-slate-500">
          <Clock className="w-3 h-3" />
          <span className="font-mono">{getTimeSince(order.created_at)}</span>
        </div>
      </div>
      <div className="p-3 space-y-1.5">
        {(order.items || []).map(i => {
          const done = ["served", "cancelled"].includes(i.item_status);
          return (
            <div key={i.id} className={cn("flex items-center gap-2 py-1", done && "opacity-50")}>
              <span className="bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded text-xs font-bold min-w-[24px] text-center">{i.quantity}×</span>
              <span className={cn("flex-1 text-sm truncate", done && "line-through")}>{i.name}</span>
              <ItemStatusPill status={i.item_status || "waiting"} />
              {i.item_status === "ready" && (
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7 px-2 text-xs"
                  onClick={() => onServe(order.id, i.id)}>
                  <Check className="w-3 h-3" />
                </Button>
              )}
            </div>
          );
        })}
        {readyCount > 1 && (
          <Button className="w-full bg-emerald-600 hover:bg-emerald-700 h-9 mt-2 text-sm font-bold"
            onClick={() => onServeAll(order)}>
            <Check className="w-4 h-4 mr-1" /> Delivered All ({readyCount})
          </Button>
        )}
      </div>
    </div>
  );
};

export default WaiterDashboard;
